import { useState, useCallback, useEffect } from 'react';
import { captureExceptionInSentry } from 'utils';
import { RecaptchaVerificationResponse, RecaptchaAPIResponse } from 'base/types/Recaptcha';
import RetryFunc from 'utils/RetryFunc';
import ProxyLayer from 'base/api/ProxyLayer';

declare global {
  interface Window {
    grecaptcha: {
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}

interface ExtendedRecaptchaVerificationResponse extends RecaptchaVerificationResponse {
  executeRecaptcha: (action?: string) => Promise<string | null>;
  getRecaptchaScore: () => Promise<number | null>;
}

// Function to load reCAPTCHA Enterprise script dynamically
const loadRecaptchaScript = (siteKey: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if script is already loaded
    if (window.grecaptcha?.enterprise) {
      resolve();
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src*="recaptcha/enterprise.js"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve());
      existingScript.addEventListener('error', reject);
      return;
    }

    // Create and load the script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

export const useRecaptchaEnterpriseVerification = (): ExtendedRecaptchaVerificationResponse => {
  const [isHumanUser, setIsHumanUser] = useState<boolean | undefined>(undefined);

  // Load reCAPTCHA script on component mount
  useEffect(() => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_ENTERPRISE_SITE_KEY;
    if (siteKey) {
      loadRecaptchaScript(siteKey).catch((error) => {
        console.error('Failed to load reCAPTCHA Enterprise script:', error);
      });
    }
  }, []);

  const executeRecaptcha = useCallback(
    async (action = 'submit'): Promise<string | null> => {
      const siteKey = process.env.REACT_APP_RECAPTCHA_ENTERPRISE_SITE_KEY || '';

      if (!siteKey) {
        console.warn('reCAPTCHA Enterprise site key not configured');
        return null;
      }

      // Ensure script is loaded
      if (!window.grecaptcha?.enterprise) {
        console.warn('reCAPTCHA Enterprise not loaded. Loading script...');
        try {
          await loadRecaptchaScript(siteKey);
        } catch (error) {
          console.error('Failed to load reCAPTCHA Enterprise script:', error);
          return null;
        }
      }

      // Check if reCAPTCHA Enterprise is loaded
      if (!window.grecaptcha?.enterprise) {
        console.warn('reCAPTCHA Enterprise not loaded. Waiting for script...');

        // Wait a bit for the script to load
        await new Promise((resolve) => setTimeout(resolve, 1000));

        if (!window.grecaptcha?.enterprise) {
          console.error('reCAPTCHA Enterprise not available after waiting');
          return null;
        }
      }

      console.log('Using reCAPTCHA Enterprise site key:', siteKey.substring(0, 10) + '...');

      try {
        await new Promise<void>((resolve) => {
          window.grecaptcha.enterprise.ready(() => {
            console.log('reCAPTCHA Enterprise ready');
            resolve();
          });
        });

        const token = await window.grecaptcha.enterprise.execute(siteKey, { action });
        return token;
      } catch (error) {
        console.error('reCAPTCHA Enterprise execution failed:', error);
        captureExceptionInSentry(`reCAPTCHA Enterprise execution failed ${error}`);
        return null;
      }
    },
    []
  );

  const getRecaptchaScore = useCallback(async (): Promise<number | null> => {
    try {
      const token = await executeRecaptcha('submit');
      if (!token) {
        return null;
      }

      const verifyToken = async (token: string): Promise<RecaptchaAPIResponse> =>
        (await RetryFunc(async () => {
          return ProxyLayer.verifyRecaptcha({ params: { token } });
        })) as Promise<RecaptchaAPIResponse>;

      const response = await verifyToken(token);
      return response?.score ?? null;
    } catch (error) {
      captureExceptionInSentry(`reCAPTCHA Enterprise score retrieval failed ${error}`);
      return null;
    }
  }, [executeRecaptcha]);

  const verifyRecaptcha = useCallback(async () => {
    const verifyToken = async (token: string): Promise<RecaptchaAPIResponse> =>
      (await RetryFunc(async () => {
        return ProxyLayer.verifyRecaptcha({ params: { token } });
      })) as Promise<RecaptchaAPIResponse>;

    try {
      const token = await executeRecaptcha('submit');
      if (!token) {
        setIsHumanUser(false);
        return;
      }

      const response = await verifyToken(token);
      const isVerificationSuccessful = response?.success && response?.score > 0.5;
      setIsHumanUser(isVerificationSuccessful);
    } catch (error) {
      setIsHumanUser(false);
      captureExceptionInSentry(`reCAPTCHA Enterprise verification failed ${error}`);
    }
  }, [executeRecaptcha]);

  return { isHumanUser, verifyRecaptcha, executeRecaptcha, getRecaptchaScore };
};
